import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useRange } from '../../range';
import { Band, Card } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import { formatCompactNumber, formatPct } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * Growth › Funnel — impression to billable click.
 *
 * This section was specified in the product doc, scheduled as Phase 7, and
 * never built — which left two other pages pointing at a hole: the Try-on
 * page and routes/tryon.py both defer "does a try-on change buy behaviour"
 * to a Funnels section that did not exist.
 *
 * Two things this page insists on, both because the product doc insists on
 * them and both easy to lose in a later edit:
 *
 *   - The last step is a **billable click**, never a "purchase" or a "sale".
 *     We never process a payment; a click is a click.
 *   - These are **event volumes, not per-user conversion**. One person
 *     appearing at two steps is counted at both. The banner says so, because
 *     a funnel diagram implies cohort tracking to almost everyone who reads
 *     one, and this is not that.
 *
 * The gaps the backend reports are rendered as a section rather than
 * silently dropped — "we can't split by surface yet, and here's exactly what
 * would make it possible" is more useful than an absent chart.
 */
function FunnelBar({ step, widthPct, isFirst }) {
  return (
    <div className="flex items-center gap-4 py-2.5">
      <div className="w-44 flex-shrink-0">
        <div className="text-sm font-medium text-ink">{step.label}</div>
        <div className="text-[11px] leading-snug text-ink-muted">{step.hint}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="h-7 w-full overflow-hidden rounded-md bg-surface-page">
          <div
            className="h-7 rounded-md bg-accent/70 transition-[width]"
            style={{ width: `${Math.max(widthPct, step.count > 0 ? 1.5 : 0)}%` }}
          />
        </div>
      </div>
      <div className="w-40 flex-shrink-0 text-right">
        <div className="text-sm font-semibold tabular-nums text-ink">{formatCompactNumber(step.count)}</div>
        <div className="text-[11px] tabular-nums text-ink-muted">
          {isFirst
            ? 'start'
            : step.pct_of_previous == null
              ? 'no prior step'
              : `${formatPct(step.pct_of_previous, 1)} of previous`}
        </div>
      </div>
    </div>
  );
}

export default function FunnelPage() {
  const range = useRange();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/funnels', { params: { days: range.days } });
      setData(data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load funnel'));
    } finally {
      setLoading(false);
    }
  }, [range.days]);

  useEffect(() => { load(); }, [load]);

  const steps = data?.steps || [];
  const first = steps[0]?.count || 0;

  const brandColumns = [
    { key: 'name', label: 'Brand', sortable: true, render: (r) => <span className="font-medium text-ink">{r.name}</span> },
    { key: 'impressions', label: 'Impressions', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.impressions) },
    { key: 'saves', label: 'Saved', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.saves) },
    { key: 'impression_to_save_pct', label: 'Impr → Save', align: 'right', sortable: true, render: (r) => formatPct(r.impression_to_save_pct, 1) },
    { key: 'save_to_tryon_pct', label: 'Save → Try-on', align: 'right', sortable: true, render: (r) => formatPct(r.save_to_tryon_pct, 1) },
    { key: 'tryon_to_billable_pct', label: 'Try-on → Billable', align: 'right', render: (r) => formatPct(r.tryon_to_billable_pct, 1) },
    { key: 'billable_clicks', label: 'Billable clicks', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.billable_clicks) },
  ];

  const categoryColumns = [
    { key: 'category', label: 'Category', sortable: true, render: (r) => <span className="font-medium text-ink">{r.category}</span> },
    { key: 'impressions', label: 'Impressions', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.impressions) },
    { key: 'impression_to_save_pct', label: 'Impr → Save', align: 'right', sortable: true, render: (r) => formatPct(r.impression_to_save_pct, 1) },
    { key: 'save_to_tryon_pct', label: 'Save → Try-on', align: 'right', sortable: true, render: (r) => formatPct(r.save_to_tryon_pct, 1) },
    { key: 'impression_to_billable_pct', label: 'Impr → Billable', align: 'right', render: (r) => formatPct(r.impression_to_billable_pct, 1) },
  ];

  const { sort: brandSort, onSort: onBrandSort } = useSort();
  const { sort: categorySort, onSort: onCategorySort } = useSort();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedBrands = useMemo(() => applySort(data?.by_brand || [], brandSort, brandColumns), [data, brandSort]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedCategories = useMemo(() => applySort(data?.by_category || [], categorySort, categoryColumns), [data, categorySort]);

  return (
    <div>
      <PageHeader
        title="Funnel"
        hint={`Impression through to billable click — ${range.label.toLowerCase()}.`}
      />

      <p className="mb-5 rounded-lg border border-hairline bg-surface px-3.5 py-2.5 text-xs leading-relaxed text-ink-secondary">
        <span className="font-medium text-ink">These are event counts, not per-user conversion.</span>{' '}
        One person appearing at two steps is counted at both. The final step is a billable click —
        a deduplicated, bot-filtered redirect to the brand's own site, which is not a purchase and
        never stands in for one.
      </p>

      {loading ? (
        <Card><p className="text-sm text-ink-muted">Loading…</p></Card>
      ) : data?.status === 'no_data' ? (
        <EmptyState
          title="No engagement recorded in this window"
          detail="The nightly rollup has no per-product rows for these dates. Widen the range, or check that the rollup job ran."
        />
      ) : (
        <>
          <Card
            title="Discovery funnel"
            hint={`${formatCompactNumber(data?.products_counted)} products with activity`}
            className="mb-8"
          >
            <div className="divide-y divide-hairline">
              {steps.map((step, i) => (
                <FunnelBar
                  key={step.key}
                  step={step}
                  isFirst={i === 0}
                  widthPct={first ? (step.count / first) * 100 : 0}
                />
              ))}
            </div>
            <p className="mt-3 text-[11px] text-ink-muted">
              Each bar is scaled against impressions; each percentage is against the step immediately
              above it, which is what makes this a funnel rather than several unrelated rates.
            </p>
          </Card>

          <Band title="By brand">
            <DataTable
              columns={brandColumns}
              rows={sortedBrands}
              rowKey={(r) => r.brand_id}
              onRowClick={(r) => navigate(`/brand/${encodeURIComponent(r.brand_id)}`)}
              sort={brandSort}
              onSort={onBrandSort}
              empty={<EmptyState title="No brand-level activity in this window" />}
            />
          </Band>

          <Band title="By category">
            <DataTable
              columns={categoryColumns}
              rows={sortedCategories}
              rowKey={(r) => r.category}
              sort={categorySort}
              onSort={onCategorySort}
              empty={<EmptyState title="No category-level activity in this window" />}
            />
          </Band>

          <Band title="Milestone event volume">
            <Card hint="Independent counts over the same window — deliberately not shown as conversion rates between steps, because they are not one.">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                {(data?.account_volume || []).map((row) => (
                  <div key={row.event}>
                    <div className="text-xs text-ink-muted">{row.label}</div>
                    <div className="text-lg font-semibold tabular-nums text-ink">
                      {formatCompactNumber(row.count)}
                    </div>
                  </div>
                ))}
              </div>
              {data?.account_days_with_data === 0 && (
                <p className="mt-3 text-xs text-ink-muted">
                  No daily event rollup exists for this window.
                </p>
              )}
            </Card>
          </Band>

          {(data?.gaps || []).length > 0 && (
            <Band title="What this page cannot show yet">
              <div className="space-y-3">
                {data.gaps.map((gap) => (
                  <div key={gap.key} className="rounded-xl border border-dashed border-baseline bg-surface px-4 py-3">
                    <div className="text-sm font-medium text-ink">{gap.title}</div>
                    <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{gap.detail}</p>
                  </div>
                ))}
              </div>
            </Band>
          )}
        </>
      )}
    </div>
  );
}
